import { useEffect, useMemo, useState } from 'react';

import { ApolloError, QueryHookOptions, useLazyQuery } from '@apollo/client';
import { isNil, noop } from 'lodash';

import { useGetOrderLazyQuery } from 'generated/graphql-gateway';
import { DeliveryStatus } from 'generated/rbi-graphql';
import useEffectOnUnmount from 'hooks/use-effect-on-unmount';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { usePrevious } from 'hooks/use-previous';
import {
  GetOrderQuery,
  IGetOrderQueryResponse,
  IGetOrderQueryVariables,
} from 'remote/queries/order';
import { useAuthGuestContext } from 'state/auth-guest';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderStatus } from 'state/order';
import { orderPollFailure, orderPollSuccessful } from 'state/order/order-state-utils';

import { handleOrderStatusChange } from './on-order-status-change';
import { IUseOrderStatusParams } from './types';

// default order status polling interval, in ms
const DEFAULT_POLL_INTERVAL = 1500;

export const useOrderStatus = ({
  failureStatuses,
  logOrderLatency,
  logger,
  onSuccess = noop,
  orderStatusPollInterval = DEFAULT_POLL_INTERVAL,
  rbiOrderId,
  successStatuses,
  skip,
  handleCommitError,
}: IUseOrderStatusParams) => {
  const { onCommitOrderFailure: onCommitGuestOrderFailure, isAuthenticated: isGuestAuthenticated } =
    useAuthGuestContext();
  const queryParams: QueryHookOptions<IGetOrderQueryResponse, IGetOrderQueryVariables> = {
    pollInterval: orderStatusPollInterval,
  };
  const isFulfillmentServiceEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_FULFILLMENT_SERVICE_GET_ORDER
  );
  const skipQuery = skip || isNil(rbiOrderId) || isFulfillmentServiceEnabled;
  const skipFulfillmentQuery = skip || isNil(rbiOrderId) || !isFulfillmentServiceEnabled;
  const [orderErrors, setOrderErrors] = useState<ApolloError | null>(null);

  if (rbiOrderId) {
    queryParams.variables = handleCommitError
      ? { rbiOrderId, handleCommitError: true }
      : { rbiOrderId };
  }

  const handleGetOrderError = (error: ApolloError) => {
    if (error.networkError) {
      // Network errors must be ignored, poll will continue retrying
      logger.warn('GetOrder polling -> Network error occurred, ignoring.');
      logger.warn(error);
    } else if (error.graphQLErrors) {
      setOrderErrors(error);
    }
  };

  const [getOrderFulfillment, lazyQueryResultFulfillment] = useGetOrderLazyQuery({
    variables: queryParams.variables,
    pollInterval: orderStatusPollInterval,
    onError: handleGetOrderError,
  });

  const [getOrder, lazyQueryResult] = useLazyQuery<IGetOrderQueryResponse, IGetOrderQueryVariables>(
    GetOrderQuery,
    {
      ...queryParams,
      onError: handleGetOrderError,
    }
  );
  const { called, stopPolling, data } = lazyQueryResult;
  const {
    stopPolling: stopPollingFulfillment,
    data: dataFulfillment,
    called: calledFulfillment,
  } = lazyQueryResultFulfillment;

  if (!skipQuery) {
    if (!called) {
      getOrder();
    }
  } else if (!skipFulfillmentQuery) {
    if (!calledFulfillment) {
      getOrderFulfillment();
    }
  }

  const serverOrder = useMemo<any | null>(() => {
    if (called) {
      return data?.order ?? null;
    }
    if (calledFulfillment) {
      return dataFulfillment?.order ?? null;
    }
    return null;
  }, [data, dataFulfillment, called, calledFulfillment]);

  const orderStatus = serverOrder && serverOrder.status;
  const orderDeliveryStatus: DeliveryStatus | null =
    serverOrder && serverOrder.delivery && serverOrder.delivery.status;

  const prevOrderStatus = usePrevious<OrderStatus | null>(orderStatus);
  const prevOrderDeliveryStatus = usePrevious<DeliveryStatus | null>(orderDeliveryStatus);

  const failure =
    !!serverOrder &&
    serverOrder.rbiOrderId === rbiOrderId &&
    orderPollFailure({
      cateringFailureStatus: failureStatuses.catering,
      deliveryFailureStatus: failureStatuses.delivery,
      order: serverOrder,
      orderFailureStatus: failureStatuses.pos,
    });

  if (failure) {
    if (serverOrder && isGuestAuthenticated()) {
      onCommitGuestOrderFailure(serverOrder);
    }
  }

  // update with catering
  const success =
    !!serverOrder &&
    serverOrder.rbiOrderId === rbiOrderId &&
    orderPollSuccessful({
      cateringSuccessStatus: successStatuses.catering,
      deliverySuccessStatus: successStatuses.delivery,
      order: serverOrder,
      orderSuccessStatus: successStatuses.pos,
    });

  useEffect(() => {
    if (skipQuery || success || failure) {
      if (called) {
        stopPolling();
      }
    }
    if (skipFulfillmentQuery || success || failure) {
      if (calledFulfillment) {
        stopPollingFulfillment();
      }
    }
  }, [
    skipQuery,
    skipFulfillmentQuery,
    success,
    failure,
    stopPolling,
    stopPollingFulfillment,
    called,
    calledFulfillment,
  ]);

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    handleOrderStatusChange({
      failure,
      logOrderLatency,
      logger,
      onSuccess,
      orderStatus,
      orderDeliveryStatus,
      prevOrderStatus,
      prevOrderDeliveryStatus,
      serverOrder,
      success,
    });
  }, [
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
  ]);

  useEffectOnUnmount(() => {
    if (called) {
      stopPolling();
    }
    if (calledFulfillment) {
      stopPollingFulfillment();
    }
  });

  return {
    ...(skipFulfillmentQuery ? lazyQueryResult : lazyQueryResultFulfillment),
    failure,
    orderStatus,
    orderDeliveryStatus,
    prevOrderStatus,
    prevOrderDeliveryStatus,
    serverOrder,
    success,
    orderErrors,
  };
};
